import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RequestBodyContact } from '../../../core/models/request-body.model';
import { CriteriaToRequestService } from '../../../shared/services/criteria-to-request.service';
import { ResultsService } from '../../../shared/services';
import { BaseApiHandleOptions, BaseApiService } from '../../../shared/services/api';
import { ResultPageDto } from 'src/app/shared/dto';
import { cloneDeep } from 'lodash-es';

@Injectable({
    providedIn: 'root'
})
export class ContactsService {
    contactListScrollPosition = 0;
    private _requestResearchData: Array<{ id: string, status: string }> = [];
    contactLoadedFlag = new BehaviorSubject(false);
    listLoadedFlag = new BehaviorSubject(false);
    createContactLoadedFlag = new BehaviorSubject(false);
    resultPage: ResultPageDto = {
        IsNavigating: false,
        resultData: [],
        verifiedData: [], // For HV and MV data only
        verifiedDataType: null, // For HV and MV data only
        totalHumanVerifiedCount: 0,
        totalPendingVerifiedCount: 0,
        contactSidebarData: null,
        contactsSortedBy: '',
        contactsSortedByDirection: ''

    };
    contactInternational = new BehaviorSubject(0);
    isContactInternational = 0;
    revealProcess = false;
    matchIdsV2 = null;
    requestBodyContact = new RequestBodyContact();
    private originalResultPage: ResultPageDto = null;

    constructor(
        private readonly baseApiService: BaseApiService,
        private readonly criteriaToRequestService: CriteriaToRequestService,
        private readonly resultsService: ResultsService,
    ) {
        this.contactInternational.subscribe((internationData) => this.isContactInternational = internationData);
        this.resultsService.checkIsNavigating$.subscribe(() => {
            this.resultPage.IsNavigating = this.resultPage.resultData.length > 0;
        });
    }

    setNavigationStatusBySearchResults() {
        this.resultPage.IsNavigating = !!this.resultPage.resultData.length;
    }


    setOriginalResultPage(value):void {
        this.originalResultPage = cloneDeep(value);
    }

    getOriginalResultPage():ResultPageDto {
        return cloneDeep(this.originalResultPage);
    }

    requestExpressResearch(matchIds: Array<string>, requestName): Observable<any> {
        return this.baseApiService.post('service/contactcloud/research/requestResearch', {
            ids: matchIds,
            expressRoD: true,
            title: requestName
        }, { showError: true });
    }

    requestVerify(matchIds: Array<string>, unmatchedContacts?: Array<string>, requestName?): Observable<any> {
        return this.baseApiService.post('service/contactcloud/research/requestResearch', {
            ids: matchIds,
            unmatched_ids: unmatchedContacts,
            title: requestName
        }, { showError: true });
    }

    revealContact(matchIds: any): Observable<any> {
        return this.baseApiService.post('service/contactcloud/search/reveal', {
            ids: matchIds
        });
        // .pipe(catchError((error) => {
        //     if (error.status === 402) {
        //         this.authenticationService.navigateToUpgrade();
        //     }
        //     return of(null);
        // }));
    }


    autoRevealContact(matchId: any, isAutoReveal) {
        if (isAutoReveal) {
            return this.baseApiService.post('service/contactcloud/search/autoReveal', {
                match_id: matchId
            });
        } else {
            return this.revealContact([matchId]);
        }
    }

    requestResearch() {
        this.baseApiService.get('service/contactcloud/research/requestResearch').subscribe((data: any) => {
            this._requestResearchData = data.contacts;
        }, (error) => {
        });
    }

    getRequestResearchData() {
        return this._requestResearchData;
    }

    updateRequestResearchData(matchId: string) {
        const isExist = this._requestResearchData.some((data) => data.id === matchId);
        if (!isExist) {
            this._requestResearchData.push({
                id: matchId,
                status: 'requested'
            });
        }
    }
    isEmpty(obj) {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                return false;
            }
        }
        return true;
    }
    reportContact(params): Observable<any> {
        return this.baseApiService.post('service/contactcloud/report', params, { showError: true });
    }

    criteriaToRequest(criteria) {
        const request = {};
        this.criteriaToRequestService.transformCopy(criteria, request, ['keyword', 'required_fields_all', 'page', 'sort_by', 'sort_direction', 'contact_revenue_min_identifier', 'contact_revenue_max_identifier', 'max_result_per_company', 'is_executive', 'gte_last_modified_date', 'owned_contacts_company', 'owned_contacts_person', 'department_size','include_primary_industry_only']);
        this.criteriaToRequestService.transformCopyWithZero(criteria, request, ['company_min_revenue', 'company_max_revenue', 'company_min_size', 'company_max_size']);
        // universal contact update
        this.criteriaToRequestService.transformArrayToCSV(criteria, request, ['company_name', 'company_location_zipcodes', 'company_domains', 'company_naics_codes', 'company_sic_codes', 'match_ids']);
        this.criteriaToRequestService.transDefaultValueCountry(criteria, request, 'location_country_codes');
        this.criteriaToRequestService.transformTiersCsv(criteria, request, 'tiers');
        this.criteriaToRequestService.transformRequiredFieldsCsv(criteria, request, 'required_fields');
        this.criteriaToRequestService.transformRankings(criteria, request, 'rankings');
        this.criteriaToRequestService.transformCopyWithNull(criteria, request, ['is_international']);
        // universal contact update
        this.criteriaToRequestService.transformIfNotNull(criteria, request, ['first_name', 'last_name', 'email', 'emails', 'matchOnJobHistory', 'company_location_zipcodes_distance', 'company_location_hq', 'company_location_branch', 'company_score_min', 'company_score_max']);
        this.criteriaToRequestService.transformArray(criteria, request, ['company_location_metro', 'lists', 'company_names', 'suppression_lists', 'location_types', 'contact_lists']);
        this.criteriaToRequestService.transformTruthMapCsv(criteria, request, ['job_levels', 'job_departments']);
        this.criteriaToRequestService.transformArrayToArray(criteria, request, 'job_sub_departments');
        this.criteriaToRequestService.transformArrayToString(criteria, request, 'company_ids', 'companyIds');

        this.criteriaToRequestService.transformArrayToString(
            criteria,
            request,
            'company_location_states',
            'abbreviation'
        );
        this.criteriaToRequestService.transformJobTitle(criteria, request, 'job_title');
        this.transformStringToArray(criteria, request, ['company_naics_codes', 'company_sic_codes']);
        this.transformArrayToStringDomains(criteria, request, 'company_names', 'company_domains');
        // Workaround
        // T1+ contacts do not have location_types defined in ElasticSearch.
        // We want to only surface T1+ contacts if the location_types contains hqFallback as that is the type for all T1+ contacts
        // To make our query surface anything if that type is searched for, we have to ensure we remove location_types in that case.
        // if (request['tiers'] !== '0' && request['location_types'] && request['location_types'].length > 0) {
        //     if (request['location_types'].includes('hqFallback')) {

        //         //delete request['location_types']; //6845
        //     }
        // }

        if (request['is_international'] === 1) { // 5092
            delete request['location_types'];
        }

        return request;
    }

    transformStringToArray(criteria, request, key) {
        if (Array.isArray(criteria[key])) {
            request[key] = criteria[key];
        } else if (typeof criteria[key] === 'string') {
            const resultArray = criteria[key] || '';
            request[key] = resultArray.split(',');
        }

        return request;
    }

    transformArrayToStringDomains(criteria, request, key, field) {
        if (typeof criteria[key] === 'string') {
            request[key] = criteria[key];
        } else {
            request[field] = (criteria[key] || []).map(function (item) {
                return item;
            }).join(',');
        }

        return request;
    }

    exportAllContact(matchIds, previewOrExport) {
        const request = {
            ids: matchIds,
            preview: previewOrExport
        };
        return this.baseApiService.post('service/contactcloud/search/export', request, {}, {
            responseType: 'text',
            observe: 'response'
        });
    }

    exportOriginalRecords(listId, listType, listName) {
        const options: BaseApiHandleOptions = {
            params: {
                listId,
                listType,
                listName
            }
        };
        return this.baseApiService.post('service/contactcloud/list/exportOriginalRecords' , '' , options, {
            responseType: 'text',
            observe: 'response'
        });
    }

    requestResearchUnmatched(params) {
        return this.baseApiService.post('service/contactcloud/list/requestResearchOriginalContacts?listId='+params.listId+'&listType='+params.listType+'&from='+params.from+'&title='+params.title, null, { showError: true });
    }

    exportAllContactInboxIntel(matchIds, mv, un, previewOrExport) {
        const request = {
            ids: matchIds,
            machine_ids: mv,
            unmatched_ids: un,
            preview: previewOrExport
        };
        return this.baseApiService.post('service/contactcloud/search/export', request, {}, {
            responseType: 'text',
            observe: 'response'
        });
    }
}
